import { useLazyQuery, useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ListingDetail } from '@deltek/specification-client/core/Models/listing'
import { Paper, TextField, Typography, makeStyles } from '@material-ui/core'
import Autocomplete, {
    AutocompleteCloseReason,
} from '@material-ui/lab/Autocomplete'
import { GetManufacturerProductPropertiesAutoSuggest } from 'api/graphQL/queries/product.queries'
import clsx from 'clsx'
import React, {
    FC,
    FocusEventHandler,
    MutableRefObject,
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { ArrowDropDown } from '@material-ui/icons'
import {
    escapeRegExp,
    stripDoubleSpaces,
    stripDoubleSpacesTrimStart,
} from 'shared/utils/inputUtils'
import listingColors from '../../../../listingColors'
import { ProductContext, ProductContextType } from '../../../ListingDetailsPage'
import { PLACEHOLDER_HEADER, PLACEHOLDER_PROPERTY } from '../constants'

const { errorColor, addPropertyButton, defaultSecondary } = listingColors

const useStyles = makeStyles((theme) => ({
    '@global': {
        '.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderRadius: 0,
            border: 'none',
            padding: 0,
            '&:focused': {
                border: 'none',
            },
            '< ::placeholder': {
                fontWeight: 'bold',
            },
        },
        '.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
            padding: theme.spacing(0, 1),
            '&::placeholder': {
                fontWeight: 'bold',
            },
            '&:hover': {
                paddingRight: theme.spacing(4),
            },
        },
        '.MuiAutocomplete-option': {
            "&[aria-disabled='true']": {
                color: errorColor,
            },
        },
        '.MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
            padding: 0,
        },
    },
    textField: {
        fontSize: pxToRem(14),
        border: 'none',
        borderRadius: 0,
    },
    autoComplete: {
        backgroundColor: theme.palette.common.white,
    },
    disabled: {
        color: errorColor,
    },
    addProperty: {
        backgroundColor: addPropertyButton,
        color: theme.palette.common.white,
        padding: theme.spacing(0.5),
        paddingLeft: theme.spacing(1),
    },
    disabledAdd: {
        backgroundColor: defaultSecondary,
        '&:hover': { cursor: 'default' },
    },
    error: {
        borderBottom: `1px solid ${errorColor}`,
    },
    errorBorder: {
        border: `1px solid ${errorColor}`,
    },
    errorLabel: {
        color: errorColor,
        fontSize: pxToRem(10),
        marginLeft: theme.spacing(1),
    },
}))

type Props = {
    defaultValue: string
    type: string
    headerText?: string
    onFocus?: FocusEventHandler
    onBlur?: FocusEventHandler
    onClose?: (
        event: React.ChangeEvent<{}>,
        reason: AutocompleteCloseReason
    ) => void
    onOpen?: (event: React.ChangeEvent<{}>) => void
    setAutocompleteValue: (value: string) => void
    errors?: Record<string, string>
    setErrors?: (errors: Record<string, string>) => void
}

const PropertyNameAutoComplete: FC<Props> = ({
    defaultValue = '',
    type = 'header',
    headerText = '',
    onFocus = () => {},
    onBlur = () => {},
    setAutocompleteValue,
    errors = {},
    setErrors = () => {},
    onClose = () => {},
    onOpen = () => {},
}) => {
    const classes = useStyles()

    const { product } = useContext<Partial<ProductContextType>>(ProductContext)

    const { sectionName, productType } = product as Partial<ListingDetail>

    const [value, setValue] = useState(defaultValue)

    let inputValueRef: MutableRefObject<string> = useRef('')

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [
        getPropertyNameAutoSuggest,
        { loading: propertyNameLoading, data: propertyNames },
    ] = useLazyQuery(GetManufacturerProductPropertiesAutoSuggest, {
        displayName: 'GetManufacturerProductPropertiesAutoSuggest',
        fetchPolicy: 'no-cache',
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on fetching data',
                    variant: 'error',
                    id: `GetManufacturerProductPropertiesAutoSuggest_FetchingError`,
                },
            })
        },
    })

    const [disabledOptions, listOptions] = useMemo(() => {
        const disabledOptions: string[] = []

        const listOptions = !!inputValueRef.current
            ? propertyNames?.manufacturerProductPropertiesAutoSuggest?.result?.flatMap(
                  (propertyGroup: any) => {
                      return [
                          ...propertyGroup.productTypePropertiesAutoSuggestResults.flatMap(
                              (property: any) => {
                                  return [
                                      ...new Set(
                                          property.autoSuggestPropertyInfo.map(
                                              (subProp: any) => {
                                                  const propertyNameArray = subProp.propertyName.split(
                                                      '|'
                                                  )

                                                  const manufacturerIdentifier = subProp.isPremiumPlus
                                                      ? '(+) '
                                                      : ''

                                                  const isInProductType =
                                                      property.productType ===
                                                          productType &&
                                                      `${propertyGroup.sectionNumber} - ${propertyGroup.sectionName}` ===
                                                          sectionName

                                                  if (type === 'header') {
                                                      if (isInProductType) {
                                                          disabledOptions.push(
                                                              `${manufacturerIdentifier}${propertyNameArray[0]
                                                                  .split('<')[0]
                                                                  .trim()}`.toLowerCase()
                                                          )
                                                      }

                                                      return `${manufacturerIdentifier}${propertyNameArray[0]
                                                          .split('<')[0]
                                                          .trim()}`
                                                  }

                                                  const propertyOption = `${
                                                      propertyNameArray[1]
                                                  } | ${
                                                      propertyNameArray[0].split(
                                                          '<'
                                                      )[0]
                                                  } | ${
                                                      property.productType
                                                  } | ${
                                                      propertyGroup.sectionNumber
                                                  } - ${
                                                      propertyGroup.sectionName
                                                  }`

                                                  if (
                                                      isInProductType &&
                                                      propertyNameArray[0]
                                                          .split('<')[0]
                                                          .trim() ===
                                                          headerText.trim()
                                                  ) {
                                                      disabledOptions.push(
                                                          `${
                                                              propertyNameArray[1]
                                                          } | ${propertyNameArray[0]
                                                              .split('<')[0]
                                                              .trim()}`.toLowerCase()
                                                      )
                                                  }

                                                  return `${manufacturerIdentifier}${propertyOption}`
                                              }
                                          )
                                      ),
                                  ]
                              }
                          ),
                      ]
                  }
              )
            : []
        return [
            disabledOptions?.sort((a: string, b: string) =>
                a.localeCompare(b, undefined, { sensitivity: 'base' })
            ),
            listOptions?.sort((a: string, b: string) =>
                a.localeCompare(b, undefined, { sensitivity: 'base' })
            ),
        ]
    }, [propertyNames, productType, sectionName])

    const getSanitizedValue = useCallback(
        (val: string) => stripDoubleSpaces(val?.replace('(+) ', '')),
        [stripDoubleSpaces]
    )

    const handleInputValueChange = useCallback(
        (e) => {
            if (e) {
                const tempValue = !!e?.target?.value ? e?.target?.value : ''

                inputValueRef.current = tempValue

                if (tempValue !== defaultValue) {
                    getPropertyNameAutoSuggest({
                        variables: {
                            input: {
                                propertyToSearch:
                                    stripDoubleSpacesTrimStart(tempValue) ?? '',
                            },
                        },
                    })
                }
            }
        },
        [
            getPropertyNameAutoSuggest,
            defaultValue,
            inputValueRef,
            stripDoubleSpacesTrimStart,
        ]
    )

    const handleAutocompleteChange = useCallback(
        (_, changeValue, reason) => {
            if (reason === 'clear') {
                setValue('')
                setAutocompleteValue('')
            } else {
                if (!!getOptionDisabled(changeValue)) {
                    setValue('')
                    setAutocompleteValue('')
                    return
                }

                if (changeValue !== defaultValue) {
                    const tempValue = getSanitizedValue(
                        type === 'property'
                            ? changeValue?.split('|')[0]
                            : changeValue
                    )

                    if (value !== tempValue) {
                        setValue(tempValue)
                    }

                    setAutocompleteValue(tempValue)
                }
            }
        },
        [setAutocompleteValue, getSanitizedValue, defaultValue, value]
    )

    const handleOnClose = useCallback(
        (e) => {
            onClose?.(e, 'blur')
        },
        [onClose]
    )

    const handleAddClick = useCallback(
        () =>
            handleAutocompleteChange({}, inputValueRef.current, 'selectOption'),
        [handleAutocompleteChange]
    )

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLDivElement>) => {
            if (!value) {
                inputValueRef.current = ''
            }
            onBlur?.(e)
        },
        [onBlur, value]
    )

    const getOptionDisabled = useCallback(
        (option) => {
            if (type === 'header' && !!option) {
                return !!disabledOptions.includes(
                    `(+) ${stripDoubleSpaces(option).replace(
                        '(+) ',
                        ''
                    )}`.toLowerCase()
                )
            }

            if (type === 'property') {
                if (!option) {
                    return true
                }

                const optionArray = option.split(' | ')

                return !!disabledOptions.includes(
                    getSanitizedValue(
                        `${optionArray[0]} | ${optionArray[1]}`
                    ).toLowerCase()
                )
            }

            return false
        },
        [listOptions, productType, sectionName, type, stripDoubleSpaces]
    )

    const filterOptions = useCallback(
        (options) => {
            if (inputValueRef.current) {
                const escapedValue = escapeRegExp(
                    inputValueRef.current?.toLowerCase()
                )
                const optionMatch = new RegExp(escapedValue)

                const filteredOptions = [
                    ...new Set(
                        options.filter((option: string) => {
                            if (type === 'header') {
                                return optionMatch.test(option.toLowerCase())
                            }

                            return optionMatch.test(
                                option.split('|')[0].toLowerCase()
                            )
                        })
                    ),
                ]

                return filteredOptions
            }
            if (!!value) {
                return [value]
            }

            return []
        },
        [disabledOptions, inputValueRef.current, value]
    )

    const options = useMemo(() => {
        if (listOptions?.length) {
            return listOptions.slice(0, 50)
        }
        return !!value ? [value] : []
    }, [listOptions, value])

    useEffect(() => {
        if (value !== defaultValue) {
            // Need to separate this from the handlechange/handleInputvaluechange to avoid removing errors prematurely
            setErrors({})
        }
    }, [value, setErrors, defaultValue])

    return (
        <>
            <Autocomplete
                className={clsx(classes.autoComplete, {
                    [classes.errorBorder]:
                        type === 'header' &&
                        !!errors &&
                        errors?.value === value,
                })}
                data-testid="propertyNameAutocompleteComboBox"
                freeSolo={false}
                blurOnSelect={true}
                clearOnBlur={true}
                aria-autocomplete="none"
                autoComplete={false}
                popupIcon={type === 'header' ? <ArrowDropDown /> : null}
                loading={propertyNameLoading}
                options={options}
                value={value}
                onChange={handleAutocompleteChange}
                onInputChange={handleInputValueChange}
                onClose={handleOnClose}
                onOpen={onOpen}
                onBlur={handleBlur}
                filterOptions={filterOptions}
                defaultValue={defaultValue}
                getOptionDisabled={getOptionDisabled}
                PaperComponent={forwardRef((props, ref) => (
                    <Paper ref={ref} {...props} style={{ width: 'inherit' }}>
                        {props.children}
                        <Typography
                            role="button"
                            onMouseDown={handleAddClick}
                            data-testid="propertyNameAutocompleteButton"
                            className={clsx(classes.addProperty, {
                                [classes.disabledAdd]: !!getOptionDisabled(
                                    type === 'header'
                                        ? inputValueRef.current
                                        : !!inputValueRef.current
                                        ? `${inputValueRef.current} | ${headerText}`
                                        : ''
                                ),
                            })}
                        >{`+ Add New ${
                            type === 'header' ? 'Header' : 'Property'
                        }`}</Typography>
                    </Paper>
                ))}
                renderInput={(params) => {
                    return (
                        <TextField
                            {...params}
                            hiddenLabel
                            placeholder={
                                type === 'header'
                                    ? PLACEHOLDER_HEADER
                                    : PLACEHOLDER_PROPERTY
                            }
                            name="dummyPN-1"
                            className={clsx(classes.textField, {
                                [classes.disabled]: params.disabled,
                            })}
                            variant="outlined"
                            onFocus={onFocus}
                            value={value}
                            inputProps={{
                                ...params.inputProps,
                                'aria-autocomplete': 'none',
                                'data-testid': 'propertyNameAutocompleteInput',
                                autoComplete: 'off',
                                maxLength: 2000,
                                required: type === 'property',
                                style: {
                                    borderBottom:
                                        !!errors &&
                                        errors?.value === value &&
                                        type !== 'header'
                                            ? `1px solid ${errorColor}`
                                            : 'none',
                                },
                            }}
                        />
                    )
                }}
            />
            {!!errors && errors.value === value && (
                <Typography component="div" className={classes.errorLabel}>
                    {errors.error}
                </Typography>
            )}
        </>
    )
}

export default PropertyNameAutoComplete
